// Az „Új megbeszélés" ISMÉTLŐDÉS-blokkja: gyakoriság · (heti / kéthetinél) a hét napjai · vég (alkalomszám VAGY
// dátum) · ELŐNÉZET („4 alkalom: 2026. nov. 3. – 2026. nov. 24."). Csak létrehozáskor jelenik meg — a sorozat
// szabálya utólag nem szerkeszthető (a BFF 422-t ad rá).
//
// Az előnézet a KÖZÖS kibontással számol (`@atlas/shared`), tehát pontosan azt mutatja, ami létrejön; a hibás
// szabály itt, mentés előtt látszik.
import { useEffect } from 'react';
import { type FieldValues, type Path, type PathValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DateField,
  MultiSegmentedField,
  SegmentedField,
  SelectField,
  TextField,
} from '@/components/shared/form-fields';
import {
  isoWeekdayOfLocal,
  previewRecurrence,
  RECURRENCE_FREQUENCIES,
  recurrenceRuleOf,
  type RecurrenceFormValues,
  usesWeekdays,
  weekdayNames,
} from '@/features/meetings/recurrence';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';

export interface MeetingRecurrenceFieldsProps {
  /** Az űrlap kezdése/vége ISO-ban (a kibontás alapja); üres → nincs előnézet. */
  startsAtIso: string;
  endsAtIso: string;
  /** A kezdés helyi alakja ('YYYY-MM-DDTHH:mm') — a heti szabály alapértelmezett napjához. */
  startLocal: string;
}

export function MeetingRecurrenceFields<TValues extends FieldValues & RecurrenceFormValues>({
  startsAtIso,
  endsAtIso,
  startLocal,
}: MeetingRecurrenceFieldsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { watch, setValue } = useFormContext<TValues>();
  const values = watch() as RecurrenceFormValues;
  const frequency = values.recurrenceFrequency;
  const weekdays = values.recurrenceWeekdays;

  // Heti / kétheti szabályra váltva a kezdő nap az alapértelmezett nap (az első alkalom maga a kezdés).
  useEffect(() => {
    if (!usesWeekdays(frequency) || weekdays.length > 0) return;
    const wd = isoWeekdayOfLocal(startLocal);
    if (wd !== null) {
      setValue('recurrenceWeekdays' as Path<TValues>, [wd] as PathValue<TValues, Path<TValues>>, {
        shouldDirty: true,
      });
    }
  }, [frequency, weekdays.length, startLocal, setValue]);

  const rule = recurrenceRuleOf(values);
  // a kibontás olcsó (legfeljebb 52 alkalom) — renderenként számoljuk, memo nélkül
  const preview = ((): { ok: boolean; text: string } | null => {
    if (rule === null || startsAtIso === '' || endsAtIso === '') return null;
    const r = previewRecurrence(startsAtIso, endsAtIso, rule);
    if (!r.ok) return { ok: false, text: t(`validation:meetings.recurrence.${r.error}`) };
    const fmt = (iso: string): string =>
      formatDateTime(iso, i18n.language, DEFAULT_TIME_ZONE, { dateStyle: 'medium' });
    return {
      ok: true,
      text: t('meetings.recurrence.preview', {
        count: r.occurrences.length,
        first: fmt(r.occurrences[0]!.startsAt),
        last: fmt(r.occurrences[r.occurrences.length - 1]!.startsAt),
      }),
    };
  })();

  const frequencyOptions = [
    { value: 'none', label: t('meetings.recurrence.frequency.none') },
    ...RECURRENCE_FREQUENCIES.map((f) => ({
      value: f,
      label: t(`meetings.recurrence.frequency.${f}`),
    })),
  ];
  const dayOptions = weekdayNames(i18n.language).map((d, i) => ({
    ...d,
    ariaLabel: weekdayNames(i18n.language, 'long')[i]!.label,
  }));

  return (
    <div className="border-border space-y-3 rounded-lg border p-3" data-testid="meeting-recurrence">
      <SelectField<TValues>
        name={'recurrenceFrequency' as Path<TValues>}
        label={t('meetings.recurrence.label')}
        options={frequencyOptions}
      />
      {frequency !== 'none' && (
        <>
          {usesWeekdays(frequency) && (
            <MultiSegmentedField<TValues>
              name={'recurrenceWeekdays' as Path<TValues>}
              label={t('meetings.recurrence.weekdays')}
              options={dayOptions}
            />
          )}
          <SegmentedField<TValues>
            name={'recurrenceEnd' as Path<TValues>}
            label={t('meetings.recurrence.end')}
            options={[
              { value: 'count', label: t('meetings.recurrence.endCount') },
              { value: 'until', label: t('meetings.recurrence.endUntil') },
            ]}
          />
          {values.recurrenceEnd === 'count' ? (
            <TextField<TValues>
              name={'recurrenceCount' as Path<TValues>}
              label={t('meetings.recurrence.countLabel')}
              inputMode="numeric"
              hint={t('meetings.recurrence.countHint')}
            />
          ) : (
            <DateField<TValues>
              name={'recurrenceUntil' as Path<TValues>}
              label={t('meetings.recurrence.untilLabel')}
            />
          )}
          {preview && (
            <p
              className={preview.ok ? 'text-muted-foreground text-sm' : 'text-destructive text-sm'}
              data-testid="recurrence-preview"
              aria-live="polite"
            >
              {preview.text}
            </p>
          )}
        </>
      )}
    </div>
  );
}
